// Amplitude wrapper, ported verbatim from the frozen legacy tree
// (js/components/event_tracker.js) to satisfy JS-3. Only the deferred
// event_tracking entrypoint constructs it (JS-19: that entry is the one
// sanctioned Amplitude loader — the SDK stays a lazy dynamic import here,
// so it still ships only in that entry's chunk).
export default class EventTracker {
  constructor({
    key = '',
    endpoint = 'https://api2.amplitude.com/2/httpapi',
    env = 'development',
    optOut = false,
    props = [],
    events = [],
    ...options
  }) {
    const isDebugEnv = ['development'].includes(env);
    const isDebugQuery = new URLSearchParams(window.location.search).get('sktrk_debug') === '1';

    this.client = null;
    this.key = key;
    this.endpoint = endpoint;
    this.optOut = optOut;
    this.options = options || {};
    this.debug = isDebugEnv || isDebugQuery;
    this.events = events || [];
    this.props = props || [];
    this.loaded = false;
    this.ready = false;
  }

  async init() {
    if (!this.debug) {
      await this.load();
      await this.client.init(this.key, {
        serverUrl: this.endpoint,
        optOut: this.optOut,
        minIdLength: 1,
        defaultTracking: {
          pageViews: false
        },
        ...this.options,
      }).promise;
    }

    this.loaded = true;

    this.callAll(this.props);
    this.callAll(this.events);

    this.ready = true;
  }

  async load() {
    if (this.client) return this.client;

    this.client = await import('@amplitude/analytics-browser');

    return this.client;
  }

  trackAmplitude(callback) {
    if (this.loaded) {
      callback.call(this);
    } else {
      this.props.push(callback);
    }
  }

  trackAmplitudeEvent(callback) {
    if (this.ready) {
      callback.call(this);
    } else {
      this.events.push(callback);
    }
  }

  callAll(collection) {
    const functions = collection.filter(item => typeof item === 'function');

    for (const fn of functions) {
      try {
        fn.call(this);
      } catch {
        // noop — one bad queued callback must not drop the rest
      }
    }

    collection.length = 0;
  }

  track(key, props, callback) {
    this.trackAmplitudeEvent(() => {
      if (this.debug) {
        console.log('EventTracker.track', key, props);
        if (typeof callback === 'function') callback.call();
      } else {
        this.client.logEvent(key, props).promise.then(callback);
      }
    });
  }

  trackClick(event, key, props) {
    event?.preventDefault();

    const href = event?.currentTarget?.getAttribute('href');
    const navigate = () => {
      if (href) window.location = href;
    };

    this.track(key, props, navigate);
  }

  setSplitTestProperty(key, name, version) {
    const value = `${name}_${version}`;

    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setSplitTestProperty', key, value);
      } else {
        const event = new this.client.Identify().set(key, value);
        this.client.identify(event);
      }
    });
  }

  setProperties(props) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setProperties', props);
      } else {
        const event = new this.client.Identify();
        for (const key in props) {
          event.set(key, props[key]);
        }
        this.client.identify(event);
      }
    });
  }

  setProperty(key, value) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setProperty', key, value);
      } else {
        const event = new this.client.Identify().set(key, value);
        this.client.identify(event);
      }
    });
  }

  setPropertiesOnce(props) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setPropertiesOnce', props);
      } else {
        const event = new this.client.Identify();
        for (const key in props) {
          event.setOnce(key, props[key]);
        }
        this.client.identify(event);
      }
    });
  }

  setPropertyOnce(key, value) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setPropertyOnce', key, value);
      } else {
        const event = new this.client.Identify().setOnce(key, value);
        this.client.identify(event);
      }
    });
  }

  setUserProperty(key, value) {
    this.setProperty(key, value);
  }

  setUserId(id) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.setUserId', id);
      } else {
        this.client.setUserId(id);
      }
    });
  }

  increment(key, value = 1) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.increment', key, value);
      } else {
        const event = new this.client.Identify().add(key, value);
        this.client.identify(event);
      }
    });
  }

  transaction(props) {
    this.trackAmplitude(() => {
      if (this.debug) {
        console.log('EventTracker.transaction', props);
      } else {
        const event = new this.client.Revenue()
          .setProductId(props.productId)
          .setPrice(props.price)
          .setQuantity(1)
          .setRevenueType(props.revenueType)
          .setEventProperties(props.eventProperties);
        this.client.revenue(event);
      }
    });
  }
}
